import React, { useReducer, useCallback, useMemo } from 'react';

import PropTypes from 'prop-types';
import { FaChevronLeft } from 'react-icons/fa';
import styled, { css } from 'styled-components';

import CloseButton from '~/shared/components/CloseButton';
import FableticsModal from '~/shared/components/FableticsModal';
import {
  TEXT_PAGE,
  ICON_PAGE,
  CONFIRMATION_PAGE,
  // TEXT_PAGE_ID,
  // ICON_PAGE_ID,
  LOGO,
  Progress,
  StateFields,
  PersonalizationTypes,
} from '~/shared/utils/embroidery';
import { Modal, mobile, Button } from '~/techstyle-shared/react-components';
import {
  defineMessages,
  useIntl,
  FormattedMessage,
} from '~/techstyle-shared/react-intl';
import { useProductActions } from '~/techstyle-shared/react-products';

import EmbroideryConfirmation from '../EmbroideryConfirmation';
import EmbroideryIconSelection from '../EmbroideryIconSelection';
import { EmbroideryProgressBar } from '../EmbroideryProgressBar/EmbroideryProgressBar';
import EmbroideryTextSelection from '../EmbroideryTextSelection';
import { useProductDetailEmbroideryContext } from '../ProductDetailCTAs/ProductDetailEmbroideryContext';

import { actions, reducer, initState } from './state';

const messages = defineMessages({
  modalTitle: {
    id: 'global_cta.embroidery_modal_title',
    defaultMessage: 'fabletics embroidery modal',
  },
});

const contentWrapperStyles = css`
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  ${mobile`
    margin: 16px;
  `};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 800px;
  text-align: center;
  ${({ $isCollapsibleContent }) =>
    $isCollapsibleContent &&
    css`
      width: auto;
    `}
`;

const Title = styled.div`
  ${({ theme }) => theme.typography.values.heading6}

  margin-top: 16px;
`;

const Subtitle = styled.div`
  ${({ theme }) => theme.typography.variants.embroideryModalCounter.textStyle}
  font-weight: 600;

  margin: 8px 0 16px;
  color: ${({ theme: { colors } }) => colors.textLightAlternate};
`;

const MainContent = styled.div`
  overflow: auto;
`;

const ModalCloseButton = styled(CloseButton)`
  position: absolute;
  top: 0px;
  right: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px;
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.closeButton};
`;

const BackIcon = styled(FaChevronLeft).attrs({
  size: 18,
})``;

const BackButton = styled(Button).attrs({ variant: 'unstyled' })`
  position: absolute;
  top: 0px;
  left: 0px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px;
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.unfocusedAction};
`;
export default function EmbroideryModal({
  dialogProps,
  isPlusSizeSelected,
  isLoading,
  onCTAClick,
  onExit,
  ...rest
}) {
  const intl = useIntl();

  const { textEmbroideryEnabled, textPersonalizations, iconPersonalizations } =
    useProductDetailEmbroideryContext();

  const productActions = useProductActions();

  // icon embroidery is not enabled(second parameter)
  const pages = [
    textEmbroideryEnabled ? TEXT_PAGE : false,
    false,
    CONFIRMATION_PAGE,
  ].filter(Boolean);

  const closeLabel = intl.formatMessage({
    id: 'global_cta.close',
    defaultMessage: 'Close',
  });

  const [
    {
      textCustomizations,
      iconCustomizations,
      previewImages,
      previewError,
      previewImageLoading,
      currentIdx,
    },
    dispatch,
  ] = useReducer(
    reducer,
    { textPersonalizations, iconPersonalizations },
    initState
  );

  // go back to previous step
  const handleBackButton = () =>
    dispatch(actions.handleProgress(Progress.BACK));

  // go back to first step and clear any existing state values
  const handleStartOver = () => {
    dispatch(actions.handleProgress(Progress.RESET));
  };

  // skip step and reset any potential values that might be defined for this step
  const handleSkip = (type) => {
    // We should always pass the PersonalizationType to the skip fn.
    // Scenario would be a user has set some customizations and moved forward a step,
    // they change their mind, go back and clear the customization
    // (which would disable "save and continue" btn) or go back and click skip.
    // We need to reset the customization options in top-level state.
    const skipValues =
      type === PersonalizationTypes.TEXT
        ? {
            [StateFields.TEXT_CUSTOMIZATIONS]: {
              [StateFields.LINE_1]: '',
              [StateFields.LINE_2]: '',
              [StateFields.COLOR]: '',
              [StateFields.FONT]: '',
              [StateFields.TEXT_LOCATION]:
                textCustomizations[StateFields.TEXT_LOCATION],
            },
          }
        : {
            [StateFields.ICON_CUSTOMIZATIONS]: {
              [StateFields.ICON]: null,
              [StateFields.ICON_LOCATION]:
                iconCustomizations[StateFields.ICON_LOCATION],
            },
          };

    dispatch(actions.handleEmbroideryConfigSubmit(skipValues));
  };

  // update top-level state and proceed to next step
  const handleEmbroideryConfigSubmit = (newEmbroideryConfig) => {
    dispatch(actions.handleEmbroideryConfigSubmit(newEmbroideryConfig));
  };

  // request embroidery preview image and cache it using the provided previewKey
  const handleEmbroideryPreview = useCallback(
    async ({ embroideryOptions, previewKey }) => {
      if (!previewImageLoading) {
        try {
          dispatch(actions.handlePreviewImageRequest());

          const previewValue = await productActions.loadCustomizationPreview(
            embroideryOptions
          );

          const previewImage = previewValue?.payload?.url
            ? `${previewValue.payload.base64}`
            : null;

          dispatch(
            actions.handlePreviewImageResponse({
              previewKey,
              previewImageSrc: previewImage,
              error: !previewImage,
            })
          );
        } catch (e) {
          dispatch(
            actions.handlePreviewImageResponse({
              error: true,
            })
          );
        }
      }
    },
    [previewImageLoading, productActions]
  );

  const makePersonalizationPayload = () => {
    let iconPersonalizationPayload = null;
    let textPersonalizationPayload = null;

    if (textCustomizations.line1) {
      const { textLocation, ...restTextCustomizations } = textCustomizations;

      textPersonalizationPayload = {
        templateProductId: textPersonalizations.productId,
        template: textPersonalizations.templateLabel,
        templateId: textPersonalizations.customizationTemplateId,
        locationId: textLocation,
        customizations: Object.entries(restTextCustomizations).map(
          ([key, value]) => ({ customizationId: key, value })
        ),
      };
    }

    if (iconCustomizations.icon) {
      iconPersonalizationPayload = {
        templateProductId: iconPersonalizations.productId,
        template: iconPersonalizations.templateLabel,
        templateId: iconPersonalizations.customizationTemplateId,
        locationId: iconCustomizations.iconLocation,
        customizations: [
          { customizationId: LOGO, value: iconCustomizations.icon.optionId },
        ],
      };
    }

    return [iconPersonalizationPayload, textPersonalizationPayload].filter(
      Boolean
    );
  };

  const onSubmit = () => {
    onCTAClick(makePersonalizationPayload());
  };

  // go back to first step, clear any existing state values and close modal
  const onCancelAndClose = () => {
    handleStartOver();
    onExit();
  };

  // icon options by location id and array of all location ids
  const { iconOptionsMap, iconLocationOptions } = useMemo(() => {
    const ret = {};

    const optionMap = Array.isArray(iconPersonalizations?.locations)
      ? iconPersonalizations.locations.reduce(
          (acc, { locationId, customizations }) => {
            const found = customizations.find(
              ({ customizationId }) => customizationId === LOGO
            );
            if (found) {
              acc[locationId] = found.options;
            }
            return acc;
          },
          ret
        )
      : ret;

    return {
      iconOptionsMap: optionMap,
      iconLocationOptions: Object.keys(optionMap).sort(
        (a, b) => parseInt(b, 10) - parseInt(a, 10)
      ),
    };
  }, [iconPersonalizations]);

  // text options by location id and array of all location ids
  const { textOptionsMap, textLocationOptions } = useMemo(() => {
    const ret = {};

    const optionsMap = Array.isArray(textPersonalizations?.locations)
      ? textPersonalizations.locations.reduce(
          (acc, { locationId, customizations }) => {
            acc[locationId] = customizations.reduce((acc, c) => {
              if (c.options) {
                acc[c.customizationId] = c.options.map((o) => ({
                  name: o.label,
                  value: o.label,
                }));
              }

              return acc;
            }, {});
            return acc;
          },
          ret
        )
      : ret;

    return {
      textOptionsMap: optionsMap,
      textLocationOptions: Object.keys(optionsMap).sort(
        (a, b) => parseInt(b, 10) - parseInt(a, 10)
      ),
    };
  }, [textPersonalizations]);

  const showBackButton = currentIdx > 0;
  const currentPage = pages[currentIdx];

  return (
    <FableticsModal
      as={Modal}
      dialogProps={dialogProps}
      title={intl.formatMessage(messages.modalTitle)}
      showCloseButton
      closeButton={
        <ModalCloseButton
          aria-label={closeLabel}
          data-autotag="pdp_embroidery_Modal_Close_btn"
        />
      }
      onExit={onCancelAndClose}
      contentWrapperStyles={contentWrapperStyles}
      dialogStyle="padding: 0"
      {...rest}
    >
      {showBackButton && (
        <BackButton
          onClick={handleBackButton}
          isLoading={isLoading}
          data-autotag="pdp_embroidery_back_btn"
        >
          <BackIcon />
        </BackButton>
      )}
      <ContentWrapper>
        <Title>
          <FormattedMessage
            id="site_pdp.embroider_title"
            defaultMessage="Add Custom Embroidery"
          />
        </Title>
        <Subtitle>
          Page {currentIdx + 1}{' '}
          <FormattedMessage id="global_cta.of" defaultMessage="of" />{' '}
          {pages.length}
        </Subtitle>
        <EmbroideryProgressBar
          numPages={pages.length}
          currentIdx={currentIdx}
        />
        <MainContent>
          {currentPage === TEXT_PAGE && (
            <EmbroideryTextSelection
              textCustomizations={textCustomizations}
              textOptions={textOptionsMap}
              locationOptions={textLocationOptions}
              isLoading={isLoading}
              onCTAClick={handleEmbroideryConfigSubmit}
              handleEmbroideryPreview={handleEmbroideryPreview}
              canSkip={false}
              onSkip={handleSkip}
              previewError={previewError}
              previewLoading={previewImageLoading}
              previewImages={previewImages}
            />
          )}
          {currentPage === ICON_PAGE && (
            <EmbroideryIconSelection
              handleEmbroideryPreview={handleEmbroideryPreview}
              iconOptions={iconOptionsMap}
              locationOptions={iconLocationOptions}
              isLoading={isLoading}
              onCTAClick={handleEmbroideryConfigSubmit}
              onSkip={handleSkip}
              iconCustomizations={iconCustomizations}
              previewError={previewError}
              previewLoading={previewImageLoading}
              previewImages={previewImages}
            />
          )}
          {currentPage === CONFIRMATION_PAGE && (
            <EmbroideryConfirmation
              isLoading={isLoading}
              onCancel={onCancelAndClose}
              onCTAClick={onSubmit}
              hasTextCustomizations={
                !!(textCustomizations.line1 || textCustomizations.line2)
              }
              hasIconCustomizations={!!iconCustomizations.icon}
              handleStartOver={handleStartOver}
              didSkip={false}
            />
          )}
        </MainContent>
      </ContentWrapper>
    </FableticsModal>
  );
}

EmbroideryModal.propTypes = {
  dialogProps: PropTypes.object,
  isLoading: PropTypes.bool,
  isPlusSizeSelected: PropTypes.bool,
  onCTAClick: PropTypes.func,
  onExit: PropTypes.func,
};
